/**
 * WebSocket 客户端通信模块
 * 基于 azt demo 优化，采用回调函数模式
 */

import { clientApi as clientApiGet, clientApiPost, standCheckClient, checkClient } from '@/assets/js/clientCommunication.js'

// WebSocket 变量
let ws
let isConnected = false
let wsCallback = null
let business = null
let wsUrl = 'ws://127.0.0.1:36718'
let wssUrl = 'wss://localhost.easysign.cn:36719'
let useHttp = false

/**
 * 连接到 WebSocket 服务器
 * @param {Function} callback - 连接结果回调函数 (errorCode) => {}，0表示成功，1表示失败
 */
export function ESAConnect(callback) {
  console.log('[WebSocket] 尝试连接... isConnected:', isConnected, 'useHttp:', useHttp)

  // 已连接，直接返回成功
  if (isConnected) {
    console.log('[WebSocket] 已有连接，跳过')
    if (typeof callback === 'function') {
      callback(0)
    }
    return
  }

  // 已确定使用 HTTP，直接返回成功（降级模式）
  if (useHttp) {
    console.log('[WebSocket] 使用 HTTP 降级模式')
    if (typeof callback === 'function') {
      callback(0)
    }
    return
  }

  const isHttps = document.location.protocol == 'https:'
  const targetUrl = isHttps ? wssUrl : wsUrl
  console.log('[WebSocket] 正在连接:', targetUrl)

  if (isHttps) {
    ws = new WebSocket(wssUrl)
  } else {
    ws = new WebSocket(wsUrl)
  }

  // 连接成功
  ws.onopen = () => {
    isConnected = true
    console.log('[WebSocket] ✅ 连接成功!')
    if (typeof callback === 'function') {
      callback(0)
    }
  }

  // 接收消息
  ws.onmessage = (event) => {
    console.log('[WebSocket] 收到消息:', event.data)
    if (typeof wsCallback === 'function') {
      try {
        const jsonParam = JSON.parse(event.data)
        wsCallback(jsonParam.error, jsonParam.msg, jsonParam.parameter, business)
      } catch (e) {
        console.error('[WebSocket] 解析消息失败:', e)
      }
    }
  }

  // 连接关闭
  ws.onclose = () => {
    isConnected = false
    console.log('[WebSocket] 连接已关闭')
  }

  // 连接错误
  ws.onerror = (error) => {
    console.log('[WebSocket] ❌ 连接失败，降级到 HTTP:', error)
    useHttp = true
    // 连接失败也返回成功（降级到HTTP）
    if (typeof callback === 'function') {
      callback(0)
    }
  }
}

/**
 * 断开 WebSocket 连接
 * @param {Function} callback - 断开结果回调函数
 */
export function ESADisconnect(callback) {
  if (isConnected) {
    const originalOnClose = ws.onclose
    const originalOnError = ws.onerror

    ws.onclose = function() {
      ws.onclose = originalOnClose
      isConnected = false
      if (typeof callback === 'function') {
        callback(0)
      }
      if (originalOnClose) originalOnClose.call(ws)
    }

    ws.onerror = function(error) {
      ws.onerror = originalOnError
      if (typeof callback === 'function') {
        callback(1)
      }
      if (originalOnError) originalOnError.call(ws, error)
    }

    try {
      ws.close()
    } catch (error) {
      if (typeof callback === 'function') {
        callback(1)
      }
    }
  } else {
    if (typeof callback === 'function') {
      callback(0)
    }
  }
}

/**
 * 发送 WebSocket 消息
 * @param {string} message - 要发送的消息
 */
function sendWebSocketMessage(message) {
  if (isConnected) {
    ws.send(message)
    console.log('[WebSocket] 发送消息:', message)
  } else {
    console.error('[WebSocket] 未连接，无法发送消息')
  }
}

/**
 * 获取当前是否使用 HTTP 模式
 */
export function isUsingHttp() {
  return useHttp
}

/**
 * 检查 UKey 是否插入
 * @param {Function} func - 回调函数 (status, msg, retpar, params)
 * @param {any} param - 业务参数
 */
export function ESACheckIsKey(func, param) {
  const isHttps = document.location.protocol == 'https:'

  if (!isConnected && !useHttp) {
    ESAConnect((errorCode) => {
      if (errorCode === 0) {
        if (useHttp) {
          // HTTP 降级模式
          clientApiGet({ command: 113, code: 0, parameter: '' })
            .then(res => func(0, '', res.parameter, param))
            .catch(err => func(1, err, null, param))
        } else {
          const data = { command: 113, code: 0, parameter: '' }
          business = param
          wsCallback = func
          sendWebSocketMessage(JSON.stringify(data))
        }
      } else {
        func(1, '连接socket服务失败', null, param)
      }
    })
  } else if (useHttp) {
    clientApiGet({ command: 113, code: 0, parameter: '' })
      .then(res => func(0, '', res.parameter, param))
      .catch(err => func(1, err, null, param))
  } else {
    const data = { command: 113, code: 0, parameter: '' }
    business = param
    wsCallback = func
    sendWebSocketMessage(JSON.stringify(data))
  }
}

/**
 * 获取签名证书
 * @param {number} CertType - 证书类型：1-签名证书，2-加密证书
 * @param {Function} func - 回调函数 (status, msg, retpar, params)
 * @param {any} param - 业务参数
 */
export function BaseGetCert(CertType, func, param) {
  const command = 101
  const code = 5
  const input = [CertType.toString()]
  const data = { command, code, parameter: input }

  if (!isConnected && !useHttp) {
    ESAConnect((errorCode) => {
      if (errorCode === 0) {
        if (useHttp) {
          clientApiGet({ command, code, parameter: input })
            .then(res => func(0, '', res.parameter, param))
            .catch(err => func(1, err, null, param))
        } else {
          business = param
          wsCallback = func
          sendWebSocketMessage(JSON.stringify(data))
        }
      } else {
        func(1, '连接socket服务失败', null, param)
      }
    })
  } else if (useHttp) {
    clientApiGet({ command, code, parameter: input })
      .then(res => func(0, '', res.parameter, param))
      .catch(err => func(1, err, null, param))
  } else {
    business = param
    wsCallback = func
    sendWebSocketMessage(JSON.stringify(data))
  }
}

/**
 * 获取客户端版本号
 * @param {Function} func - 回调函数
 * @param {any} param - 业务参数
 */
export function ESAGetClientVersion(func, param) {
  const command = 114
  const code = 10
  const input = ['']
  const data = { command, code, parameter: input }

  if (!isConnected && !useHttp) {
    ESAConnect((errorCode) => {
      if (errorCode === 0) {
        if (useHttp) {
          checkClient()
            .then(res => func(0, '', res.parameter, param))
            .catch(err => func(1, err, null, param))
        } else {
          business = param
          wsCallback = func
          sendWebSocketMessage(JSON.stringify(data))
        }
      } else {
        func(1, '连接socket服务失败', null, param)
      }
    })
  } else if (useHttp) {
    checkClient()
      .then(res => func(0, '', res.parameter, param))
      .catch(err => func(1, err, null, param))
  } else {
    business = param
    wsCallback = func
    sendWebSocketMessage(JSON.stringify(data))
  }
}

/**
 * 摘要数组签名（根据支持的摘要算法选择）
 * @param {string} hashdata - 摘要数据
 * @param {Function} func - 回调函数
 * @param {any} param - 业务参数
 */
export function BaseSignByAllHash(hashdata, func, param) {
  const command = 101
  const code = 19
  const input = [hashdata.toString()]
  const data = { command, code, parameter: input }

  if (!isConnected && !useHttp) {
    ESAConnect((errorCode) => {
      if (errorCode === 0) {
        if (useHttp) {
          clientApiPost({ command, code, parameter: input })
            .then(res => func(0, '', res.parameter, param))
            .catch(err => func(1, err, null, param))
        } else {
          business = param
          wsCallback = func
          sendWebSocketMessage(JSON.stringify(data))
        }
      } else {
        func(1, '连接socket服务失败', null, param)
      }
    })
  } else if (useHttp) {
    clientApiPost({ command, code, parameter: input })
      .then(res => func(0, '', res.parameter, param))
      .catch(err => func(1, err, null, param))
  } else {
    business = param
    wsCallback = func
    sendWebSocketMessage(JSON.stringify(data))
  }
}

/**
 * 通用客户端 API 调用（Promise 模式，兼容之前的代码）
 * @param {Object} param - 请求参数 { command, code, parameter, httpMethod }
 * @returns {Promise} 请求结果
 */
export function clientApi(param) {
  return new Promise((resolve, reject) => {
    const command = param.command
    const code = param.code
    const parameters = param.parameter
    const httpMethod = param.httpMethod

    if (!isConnected && !useHttp) {
      ESAConnect((errorCode) => {
        if (errorCode === 0) {
          if (useHttp) {
            // HTTP 降级模式
            executeHttpRequest(command, code, parameters, httpMethod, resolve, reject)
          } else {
            // WebSocket 模式
            executeWsRequest(command, code, parameters, resolve, reject)
          }
        } else {
          reject('连接socket服务失败')
        }
      })
    } else if (useHttp) {
      executeHttpRequest(command, code, parameters, httpMethod, resolve, reject)
    } else {
      executeWsRequest(command, code, parameters, resolve, reject)
    }
  })
}

/**
 * 执行 HTTP 请求
 */
function executeHttpRequest(command, code, parameters, httpMethod, resolve, reject) {
  const param = { command, code, parameter: parameters }

  // 特殊处理：检查客户端
  if (command == 113 && code == 11) {
    standCheckClient()
      .then(resolve)
      .catch(reject)
  } else if (command == 114 && code == 10) {
    checkClient()
      .then(resolve)
      .catch(reject)
  } else {
    const apiCall = (httpMethod == 'POST' || httpMethod == 'post') ? clientApiPost : clientApiGet
    apiCall(param)
      .then(resolve)
      .catch(reject)
  }
}

/**
 * 执行 WebSocket 请求
 */
function executeWsRequest(command, code, parameters, resolve, reject) {
  const data = { command, code, parameter: parameters }
  console.log('[WebSocket] 发送请求:', data)

  wsCallback = (error, msg, retpar, params) => {
    if (error == 0) {
      resolve({ error, msg, parameter: retpar })
    } else {
      reject(msg)
    }
  }

  sendWebSocketMessage(JSON.stringify(data))
}

/**
 * 获取签名证书（Promise 模式）
 * @param {string|number} CertType - 证书类型 (1:签名证书, 2:加密证书)
 * @returns {Promise} 证书数据
 */
export function ESAReaderCert(CertType) {
  return clientApi({ command: 101, code: 5, parameter: [CertType.toString()] })
}



// WEBPACK FOOTER //
// ./src/assets/js/websocketFunctions.js